import React, { useCallback, useEffect, useState } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../misc/Card";
import { Label } from "../../misc/Label";
import { Switch } from "../../misc/Switch";
import { Button } from "../../misc/Button";
import { ArrowLeft, Store } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useUploadNavigationGuard } from "../../../hooks/useUploadNavigationGuard";
import ReconfirmOverlay from "../../misc/ReconfirmOverlay";
import { cn } from "../../../utils/classMerger";
import { getAuthHeader } from "../../../utils/authHeader";
import axios from "axios";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import { AutopilotResponse } from "../../../types/autopilot";

// MB-1481: a standalone Settings home for commercial-listing preferences.
// Today that's just the pricing switch, moved here from the Autopilot
// section (AutopilotSettingsFields still renders it in the MLS setup wizard
// via hideCommercialSwitch=false there). The PATCH is a partial update
// (UpdateAutopilotSettingsDto — every field optional), so saving here only
// ever touches commercialUsePrice; the rest of the account's Autopilot
// settings are untouched.
const Commercial: React.FC = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile(1200);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const [staged, setStaged] = useState<boolean | null>(null);
    // The last value the server confirmed, for dirty detection.
    const [saved, setSaved] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [leaveOverlay, setLeaveOverlay] = useState(false);
    const [forceLeave, setForceLeave] = useState(false);

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            const headers = await getAuthHeader();
            const response = await axios.get<AutopilotResponse>(
                `${backendUrl}/settings/autopilot`,
                { headers }
            );
            // Server-defaulted true — undefined reads as on.
            const current =
                response.data.settings.commercialUsePrice !== false;
            setStaged(current);
            setSaved(current);
        } catch (error) {
            console.error("Failed to load Commercial settings:", error);
            toast.error(
                <CustomToast
                    message="Failed to load Commercial settings"
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    }, [backendUrl]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const isDirty =
        !loading && staged !== null && saved !== null && staged !== saved;

    // Guard ALL in-app navigation away from this screen while there are unsaved
    // changes (same pattern as Autopilot and MlsDetails): the shared hook
    // intercepts programmatic navigate() AND the browser back/forward buttons;
    // `beforeunload` covers refresh / tab close. Both ways off this page - the
    // Back to Settings button and the "Manage property types" link - sit above
    // the Save button, so without this a toggled switch is lost in silence.
    //
    // NOTE: the hook arms its browser-back "sentinel" only when its effect
    // re-runs with count > 0, and that effect keys on `onNavigationAttempt` - so
    // this callback MUST change when `isDirty` flips (hence the [isDirty] dep).
    // A stable ([]) callback would leave the browser-back button unguarded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onNavigationAttempt = useCallback(() => setLeaveOverlay(true), [isDirty]);
    const { pendingNavigation, confirmNavigation, cancelNavigation } =
        useUploadNavigationGuard({
            uploadCount: isDirty && !forceLeave ? 1 : 0,
            onNavigationAttempt,
        });

    // Browser back/forward doesn't carry a target, so confirmNavigation() can't
    // complete it. When the user confirms "Leave" on a back-button attempt we
    // drop the guard (forceLeave -> count 0) and navigate to Settings here.
    useEffect(() => {
        if (forceLeave) {
            navigate("/settings");
        }
    }, [forceLeave, navigate]);

    useEffect(() => {
        if (!isDirty) return;
        const handler = (e: BeforeUnloadEvent): void => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    const handleSave = async () => {
        if (staged === null || saving) return;
        try {
            setSaving(true);
            const headers = await getAuthHeader();
            const response = await axios.patch<AutopilotResponse>(
                `${backendUrl}/settings/autopilot`,
                { commercialUsePrice: staged },
                { headers }
            );
            const persisted =
                response.data.settings.commercialUsePrice !== false;
            setStaged(persisted);
            setSaved(persisted);
            toast.success(
                <CustomToast
                    message="Settings saved. Applies to Auto Pilot campaigns on commercial listings at their next weekly renewal."
                    type="success"
                />,
                { autoClose: 5000 }
            );
        } catch (error) {
            console.error("Failed to update Commercial settings:", error);
            const message =
                axios.isAxiosError(error) && error.response?.data?.message
                    ? String(error.response.data.message)
                    : "Failed to update Commercial settings";
            toast.error(<CustomToast message={message} type="error" />, {
                autoClose: 4000,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card
            className={cn(
                "w-full max-w-4xl mx-auto",
                isMobile ? "h-full m-0 rounded-none" : "h-auto"
            )}
        >
            <div className="p-4">
                <Button
                    id="back-to-settings-btn"
                    onClick={() => navigate("/settings")}
                    className="mb-4 flex items-center"
                >
                    <ArrowLeft className="mr-2 h-5 w-5" /> Back to Settings
                </Button>
            </div>
            <CardHeader className="mb-0 pb-0">
                <CardTitle className="flex items-center">
                    <Store className="h-6 w-6 mr-2" /> Commercial
                </CardTitle>
                <CardDescription>
                    Preferences for office, retail, industrial, land, and
                    other non-residential listings.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pb-8">
                {loading || staged === null ? (
                    <div className="space-y-4">
                        <div className="w-full h-6 bg-gray-200 rounded animate-skeleton" />
                        <div className="w-2/3 h-6 bg-gray-200 rounded animate-skeleton" />
                    </div>
                ) : (
                    <>
                        <div className="bg-gray-100 p-4 rounded-md space-y-3">
                            <p className="text-sm text-gray-600">
                                Marabot recognizes commercial, investor, and
                                agricultural listings and writes
                                category-appropriate posts for them instead of
                                residential language - office, retail,
                                industrial, land, farm, and business-for-sale
                                listings each get their own voice.
                            </p>
                            <p className="text-sm text-gray-600">
                                Commercial property types are opt-in - none of
                                them sync from your MLS until you turn them on
                                for auto-sync.
                            </p>
                            <Button asChild variant="outv3" size="sm">
                                <Link to="/settings/mls-details">
                                    Manage property types
                                </Link>
                            </Button>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex flex-col flex-[0.8]">
                                <Label
                                    htmlFor="commercial-use-price"
                                    className="font-semibold text-primary"
                                >
                                    Include pricing in commercial posts
                                </Label>
                                <span className="text-sm text-neutral-500 mt-2">
                                    Applies to Auto Pilot campaigns on
                                    commercial listings at their next weekly
                                    renewal.
                                </span>
                            </div>
                            <Switch
                                id="commercial-use-price"
                                checked={staged}
                                disabled={saving}
                                onClick={() => setStaged(!staged)}
                            />
                        </div>

                        <div className="bg-gray-100 p-3 rounded-md">
                            <p className="text-sm text-gray-600">
                                <strong className="text-primary">
                                    Note:
                                </strong>{" "}
                                Per-listing pricing overrides are set on each
                                listing's campaign card and take precedence
                                over this account-wide default.
                            </p>
                        </div>

                        <div className="border-t border-gray-100 pt-6">
                            <Button
                                id="commercial-save-btn"
                                onClick={handleSave}
                                disabled={saving}
                                className={cn(isMobile && "w-full")}
                            >
                                Save Changes
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>

            <ReconfirmOverlay
                isOpen={leaveOverlay}
                onClose={() => {
                    setLeaveOverlay(false);
                    cancelNavigation();
                }}
                onConfirm={() => {
                    setLeaveOverlay(false);
                    // Programmatic nav (the Back button, the property-types
                    // link, the sidebar) carries a target the hook can complete;
                    // browser back/forward doesn't, so fall back to forceLeave.
                    if (pendingNavigation) {
                        confirmNavigation();
                    } else {
                        setForceLeave(true);
                    }
                }}
                title="Leave without saving?"
                message="You have unsaved changes to your Commercial settings."
                messageTwo="If you leave now, your changes will be lost."
                confirmText="Leave"
                cancelText="Stay"
                reverse={true}
            />
        </Card>
    );
};

export default Commercial;
